import { App } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from 'react-oidc-context';
import { API_DATE_FORMAT } from '../utils/dateFormatUtils.ts';
import { repeatBytes } from '../utils/print.ts';
import { useCurrentContextStore } from './store/useCurrentContextStore.ts';
import { printErrorShown, usePrinter } from './usePrinter.ts';

interface PrintEmptyLabelPayload {
  doctorId: string;
  patientTitle: string;
  patientLastName: string;
  patientFirstName: string;
  patientSvnr: string;
  patientBirthday: string;
  insuranceCode: string;
}

/** `requestId` files the labels with their Anforderung where there is one. */
export const usePrintEmptyLabel = (): ((
  payload: PrintEmptyLabelPayload,
  count: number,
  onError: () => void,
  onSuccess: () => void,
  requestId?: string
) => Promise<void>) => {
  const auth = useAuth();
  const { print } = usePrinter();

  return async (payload: PrintEmptyLabelPayload, count: number, onError: () => void, onSuccess: () => void, requestId?: string) => {
    try {
      const response = await fetch(`${window._env_.API_URL}/rest/empty-label`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${auth.user?.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          ...payload,
          patientBirthday: dayjs(payload.patientBirthday).format(API_DATE_FORMAT),
        }),
      });

      if (!response.ok) {
        onError();
        return;
      }

      const labels = await repeatBytes(await response.blob().then(it => it.arrayBuffer()), count);
      if (labels.byteLength > 0) {
        await print(labels, 'label', { title: 'Leer-Etiketten', requestId });
      }
      onSuccess();
    } catch (e) {
      if (!printErrorShown(e)) {
        onError();
      }
    }
  };
};

export type EmptyLabelPatient = {
  title: string;
  lastName: string;
  firstName: string;
  svnr: string;
  birthday: string;
  insuranceCode: string;
};

export const emptyLabelPayload = (doctorId: string, patient: EmptyLabelPatient): PrintEmptyLabelPayload => ({
  doctorId,
  patientTitle: patient.title,
  patientLastName: patient.lastName,
  patientFirstName: patient.firstName,
  patientSvnr: patient.svnr,
  patientBirthday: patient.birthday,
  insuranceCode: patient.insuranceCode,
});

/** One Leer-Etikett for a patient of the current Zuweiser, with the usual feedback. */
export const usePrintEmptyLabelOf = (): ((patient: EmptyLabelPatient, requestId?: string) => Promise<void>) => {
  const printEmptyLabel = usePrintEmptyLabel();
  const { message } = App.useApp();
  const { currentDoctorId } = useCurrentContextStore();

  return (patient, requestId) =>
    printEmptyLabel(
      emptyLabelPayload(currentDoctorId, patient),
      1,
      () => message.error('Beim Drucken ist ein Fehler aufgetreten'),
      () => message.success('Leer-Etikett wurde an den Drucker gesendet'),
      requestId
    );
};
